import { ActionDropdown } from 'components';
import { ReactElement } from 'react';
import { debugId } from '../style.css';
import { toast } from 'react-toastify';
import { type Action, DialogForm } from 'components';
import { openPlanModal } from '../../../openPlanModal';
import { TextField } from '@ff-it/form';
import type { RowOrGroup } from 'modules/campaign/plan/types';
import { actionBtn } from './style.css';
import { usePlanMeta, usePlanMutations } from 'modules/campaign/plan/state';
import { MAX_GROUP_DEPTH } from 'modules/campaign/plan/const';
import { getSingleCreatedItem } from 'modules/campaign/plan/state/helpers';

export function useRowActions(row: RowOrGroup): (Action | '-')[] {
  const { planning } = usePlanMeta();
  const { copyRows, update, addRow, selectCell } = usePlanMutations();

  if (row._isGroup) {
    const locked = row.config.locked;
    // dragable, vs has actions

    return [
      {
        action: 'add-media',
        button: {
          icon: 'rectangle-ad',
          children: 'Media',
          onClick: () => addRow('MEDIA', row.id),
          disabled: !planning,
        },
      },
      {
        action: 'add-service',
        button: {
          icon: 'screwdriver-wrench',
          children: 'Service',
          onClick: () => addRow('SERVICE', row.id),
          disabled: !planning,
        },
      },
      {
        action: 'add-smart',
        button: {
          icon: 'wand-magic-sparkles',
          children: 'Smart service',
          onClick: () => addRow('SMART', row.id),
          disabled: !planning,
        },
      },
      {
        action: 'add-group',
        button: {
          icon: 'folder',
          children: 'Subgroup',
          onClick: async () => {
            const res = await update(
              {
                groups: {
                  upsert: [{ title: '', budget: null, parent_id: row.id }],
                },
              },
              (resp, prevState) => getSingleCreatedItem('groups', resp, prevState),
            );
            const group = res.unwrap();
            selectCell(`group-${group.id}`);
          },
          disabled: locked || row.groupDepth >= MAX_GROUP_DEPTH, // can add row unless locked or too deep
        },
      },
      '-',
      {
        action: 'remove',
        button: {
          children: 'Delete',
          disabled: locked || row.numChildren !== 0,
          icon: 'trash-can-xmark',
          variant: 'danger',
          onClick: async () => {
            const res = await update({ groups: { remove: [row.id] } });
            res.unwrap();
            toast.warn(`Deleted group!`);
          },
        },
      },
    ];
  }

  const locked = !planning || row.state.is_locked;

  const actions: (Action | '-')[] = [
    {
      action: 'comment',
      button: {
        children: 'Comment',
        disabled: row.config.comment.locked, // we can comment even for locked plan or row
        icon: 'comment-dots',
        onClick: () =>
          openPlanModal((props) => (
            <DialogForm
              {...props}
              initialValues={{ comment: row.comment }}
              submitHandler={async ({ comment }: any) => {
                const res = await update({ rows: { upsert: [{ id: row.id, comment }] } });
                return res.unwrapFormError();
              }}
            >
              <TextField name="comment" label="Comment" multiline required />
            </DialogForm>
          )),
      },
    },
    {
      action: 'name_product',
      button: {
        children: 'Custom name',
        disabled: row.config.product_name.locked, // we can comment even for locked plan or row
        icon: 'input-text',
        onClick: () =>
          openPlanModal((props) => (
            <DialogForm
              {...props}
              initialValues={{ product_name: row.product_name }}
              submitHandler={async ({ product_name }: any) => {
                const res = await update({ rows: { upsert: [{ id: row.id, product_name }] } });
                return res.unwrapFormError();
              }}
            >
              <TextField name="product_name" label="Custom product name" required />
            </DialogForm>
          )),
      },
    },
    {
      action: 'passthrough',
      button: {
        children: 'Toggle passthrough',
        disabled: row.config.passthrough.locked,
        icon: row.passthrough ? 'link-simple' : 'link-simple-slash',
        onClick: async () => {
          const res = await update({ rows: { upsert: [{ id: row.id, passthrough: !row.passthrough }] } });
          res.unwrap();
        },
      },
    },
    '-',
    {
      action: 'copy',
      button: {
        children: 'Copy',
        disabled: !planning,
        icon: 'copy',
        onClick: async () => {
          await copyRows([row]);
          toast.success(`Copied row!`);
        },
      },
    },
    {
      action: 'remove',
      button: {
        children: 'Delete',
        disabled: locked,
        variant: 'danger',
        icon: 'trash-can-xmark',
        onClick: async () => {
          const res = await update({
            rows: {
              remove: [row.id],
            },
          });
          res.unwrap();
          toast.warn(`Deleted row!`);
        },
      },
    },
  ];

  return actions;
}

export function RowActions({ row }: { row: RowOrGroup }): ReactElement {
  const actions = useRowActions(row);

  return (
    <ActionDropdown
      testId={`${row._key}-actions`}
      icon="ellipsis-vertical"
      actions={actions}
      menuSize="md"
      variant="ghost"
      className={actionBtn}
      after={
        row._isGroup ? undefined : (
          <div
            className={debugId}
            onClick={() => {
              navigator.clipboard.writeText(row.id.toString());
              toast.info('Row id copied to clipboard');
            }}
          >{`id: ${row.id}`}</div>
        )
      }
    />
  );
}
